/**
 * The main agent's mood, from what really happens (docs/design/APP-APPLE.md §4.3), shared by every character of the
 * page: « ecoute » while the owner writes, « reflechit » from turn.started / turn.thinking / turn.tool, « parle » while
 * turn.delta streams, « joie » after a turn that acted (a tool that succeeded), an approval accepted, or a heart or a
 * thumb up from the owner — then back to « repos ».
 */
import { useSyncExternalStore } from "react";
import type { CharacterMood } from "../../shared/character";
import { agentSocket } from "./socket";

const JOY_MS = 1800;
const PONDER_MS = 1200;
const TURN_TTL_MS = 10 * 60_000;

/** Running turns: when each started, whether text streams, whether a tool succeeded. */
const turns = new Map<string, { since: number; talking: boolean; acted: boolean }>();
let listening = false;
let flash: { mood: CharacterMood; until: number } | null = null;
let current: CharacterMood = "repos";
let timer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function compute(): CharacterMood {
  const now = Date.now();
  for (const [id, t] of turns) if (now - t.since > TURN_TTL_MS) turns.delete(id);
  if (flash && flash.until > now) return flash.mood;
  flash = null;
  if (listening) return "ecoute";
  const live = [...turns.values()];
  if (live.some((t) => t.talking)) return "parle";
  if (live.length) return "reflechit";
  return "repos";
}

function update(): void {
  const next = compute();
  if (timer) clearTimeout(timer);
  timer = flash ? setTimeout(update, Math.max(0, flash.until - Date.now()) + 10) : null;
  if (next === current) return;
  current = next;
  for (const fn of [...listeners]) fn();
}

/** A short mood on top of the rest (joie after a success, reflechit after a doubt). */
export function flashMood(mood: CharacterMood, ms: number = mood === "joie" ? JOY_MS : PONDER_MS): void {
  flash = { mood, until: Date.now() + ms };
  update();
}

/** The owner is writing (the composer has focus and text): the character listens. */
export function setListening(on: boolean): void {
  if (listening === on) return;
  listening = on;
  update();
}

agentSocket.subscribe((e) => {
  switch (e.type) {
    case "turn.started":
      turns.set(e.turnId, { since: Date.now(), talking: false, acted: false });
      break;
    case "turn.thinking":
      if (!turns.has(e.turnId)) turns.set(e.turnId, { since: Date.now(), talking: false, acted: false });
      break;
    case "turn.tool": {
      const t = turns.get(e.turnId) ?? { since: Date.now(), talking: false, acted: false };
      if (e.status !== "start" && e.status !== "error") t.acted = true;
      turns.set(e.turnId, t);
      break;
    }
    case "turn.delta": {
      const t = turns.get(e.turnId) ?? { since: Date.now(), talking: false, acted: false };
      t.talking = true;
      turns.set(e.turnId, t);
      break;
    }
    case "turn.done": {
      const t = turns.get(e.turnId);
      turns.delete(e.turnId);
      if (t?.acted && e.status === "done") flash = { mood: "joie", until: Date.now() + JOY_MS };
      break;
    }
    case "approval.upsert":
      if (e.approval.status === "approved" || e.approval.status === "executed") flash = { mood: "joie", until: Date.now() + JOY_MS };
      break;
    default:
      return;
  }
  update();
});

// A reconnection may have missed turn.done events: start from a clean slate.
agentSocket.onOpen((reconnect) => {
  if (!reconnect) return;
  turns.clear();
  update();
});

export function useMainMood(): CharacterMood {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => current,
    () => current,
  );
}
